import React, { useRef, useCallback } from "react";
import { container } from "@/utils/mobx";
import { useAction } from "@/components/toolkit/InputProvider";
import { isTouchDevice } from "@/utils/device";

const IS_TOUCH = isTouchDevice();
import { Dialog } from "@/components/toolkit/Dialog";
import { TextInput } from "@/components/toolkit/TextInput";
import { Clickable } from "@/components/toolkit/Clickable";
import { ContextMenu } from "@/components/toolkit/ContextMenu";
import { ConfirmDialog } from "@/components/toolkit/ConfirmDialog";
import { t } from "@/i18n";
import { BranchSelectStore } from "./store";
import "./s.branch_select.css";


export const BranchSelect = container((props: {
    store: BranchSelectStore,
}) => {
    const store = props.store;
    const triggerWrapRef = useRef<HTMLDivElement>(null);
    const actionsBtnRef = useRef<HTMLButtonElement>(null);

    const handleTriggerClick = useCallback(() => {
        const rect = triggerWrapRef.current?.getBoundingClientRect();
        store.toggleDropdown((rect?.bottom ?? 0) + 4, rect?.left ?? 0);
    }, []);

    const handleActionsClick = useCallback(() => {
        const rect = actionsBtnRef.current?.getBoundingClientRect();
        const trigger = triggerWrapRef.current?.getBoundingClientRect();
        store.showActionsMenu(rect?.left ?? 0, (rect?.bottom ?? 0) + 4, (trigger?.bottom ?? 0) + 4, trigger?.left ?? 0);
    }, []);

    useAction('branch-select', 'close-dropdown', store.closeDropdown, []);

    if (!store.isValidContainer) {
        return null;
    }

    const displayLabel = store.vaultSelected
        ? t("review_vault")
        : (store.currentBranch ?? t("review_no_branch"));

    return (
        <div className="branch_select">
            <span className="branch_select__icon">⎇</span>

            <div ref={triggerWrapRef} className="branch_select__trigger_wrap">
                <div className="branch_select__trigger" onClick={handleTriggerClick}>
                    <span className="branch_select__trigger_label">{displayLabel}</span>
                    {!store.vaultSelected && store.ahead > 0 && (
                        <span className="branch_select__divergence" title={t("review_commits_ahead")}>
                            ↑{store.ahead}
                        </span>
                    )}
                    {!store.vaultSelected && store.behind > 0 && (
                        <span className="branch_select__divergence" title={t("review_commits_behind")}>
                            ↓{store.behind}
                        </span>
                    )}
                    <span className="branch_select__trigger_arrow">▾</span>
                </div>

                {store.isDropdownOpen && (
                    <>
                        <div className="branch_select__backdrop" onClick={store.closeDropdown} />
                        <div
                                className="branch_select__dropdown"
                                data-input-component="branch-select"
                                data-input-focus="move"
                                style={IS_TOUCH
                                    ? { top: store.dropdownTop }
                                    : { top: store.dropdownTop, left: store.dropdownLeft }
                                }
                            >
                            <div className="branch_select__search_wrap">
                                <TextInput
                                    placeholder={t("review_branch_search_placeholder")}
                                    value={store.searchText}
                                    onChange={store.changeSearchText}
                                    autoFocus
                                />
                            </div>
                            <div className="branch_select__list">
                                {store.filteredBranches.map(branch => (
                                    <div
                                        key={branch}
                                        className={`branch_select__option${!store.vaultSelected && store.currentBranch === branch ? " active" : ""}`}
                                        onClick={() => store.selectBranch(branch)}
                                    >
                                        {branch}
                                    </div>
                                ))}
                                {store.filteredBranches.length === 0 && store.searchText && (
                                    <div className="branch_select__no_results">
                                        {t("review_branch_no_results")}
                                    </div>
                                )}
                            </div>
                            <div className="branch_select__divider" />
                            <div
                                className={`branch_select__option branch_select__option--vault${store.vaultSelected ? " active" : ""}`}
                                onClick={store.selectVault}
                            >
                                {t("review_vault")}
                            </div>
                            <div className="branch_select__divider" />
                            <div
                                className="branch_select__option branch_select__option--create"
                                onClick={store.openDialog}
                            >
                                + {t("review_new_branch_option")}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {store.isBusy ? (
                <span className="branch_select__actions_btn branch_select__actions_btn--busy">
                    <span className="branch_select__actions_spinner" />
                </span>
            ) : (
                <button
                    ref={actionsBtnRef}
                    className="branch_select__actions_btn"
                    title={t("review_actions_btn")}
                    onClick={handleActionsClick}
                >⋯</button>
            )}
            <ContextMenu store={store.actionsMenuStore} options={store.actionsMenuStore.options ?? []} />

            {store.resetDialogOpen && (
                <ConfirmDialog
                    title={t("review_reset_title")}
                    message={t("review_reset_confirm")}
                    confirmLabel={t("review_reset_btn")}
                    danger
                    onConfirm={store.confirmReset}
                    onCancel={store.cancelReset}
                />
            )}

            {store.showDialog && (
                <Dialog
                    title={t("review_new_branch_dialog_title")}
                    onClose={store.closeDialog}
                >
                    <TextInput
                        className="branch_select__input"
                        placeholder={t("review_new_branch_placeholder")}
                        value={store.newBranchName}
                        autoFocus
                        onChange={store.changeBranchName}
                        onEnter={store.createBranch}
                    />
                    <Clickable onClick={store.createBranch}>
                        <button className="branch_select__create_btn" disabled={!store.newBranchName.trim()}>
                            {t("review_create_branch")}
                        </button>
                    </Clickable>
                </Dialog>
            )}

            {store.confirmBranch && (
                <Dialog
                    title={t("review_switch_branch_title")}
                    onClose={store.cancelConfirm}
                >
                    <div className="branch_select__confirm_actions">
                        <Clickable onClick={store.confirmCheckout}>
                            <button className="branch_select__confirm_btn branch_select__confirm_btn--primary">
                                {t("review_switch_branch_switch_btn")} "{store.confirmBranch}"
                            </button>
                        </Clickable>
                        <Clickable onClick={store.confirmCheckoutCurrent}>
                            <button className="branch_select__confirm_btn branch_select__confirm_btn--secondary">
                                {t("review_switch_branch_checkout_current_btn")} ({store.currentBranch})
                            </button>
                        </Clickable>
                    </div>
                </Dialog>
            )}
        </div>
    );
}, BranchSelectStore);
